import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { RefreshCw, X, Flame } from "lucide-react";
import {
  getLiveVisitorSessions,
  getCityHeatmap,
} from "@/lib/visitor-tracking.functions";
import { LazyCustomerMap } from "@/components/admin/LazyCustomerMap";

export const Route = createFileRoute("/customer-map-live")({
  head: () => ({
    meta: [
      { title: "Customer Map (live) - AutoXChange" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: CustomerMapLivePage,
});

function CustomerMapLivePage() {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [showHeatmap, setShowHeatmap] = useState(true);
  const liveFn = useServerFn(getLiveVisitorSessions);
  const heatFn = useServerFn(getCityHeatmap);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setAuthChecked(true);
      if (!session?.user) navigate({ to: "/login" });
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setAuthChecked(true);
      if (!session?.user) navigate({ to: "/login" });
    });
    return () => subscription.unsubscribe();
  }, [navigate]);

  const liveQuery = useQuery({
    queryKey: ["customer-map-live-fullscreen"],
    queryFn: () => liveFn({ data: { windowMinutes: 5 } }),
    refetchInterval: 15_000,
    enabled: !!user,
  });

  const heatQuery = useQuery({
    queryKey: ["customer-map-heatmap-fullscreen"],
    queryFn: () => heatFn({ data: { days: 30, limit: 50 } }),
    refetchInterval: 5 * 60_000,
    enabled: !!user,
  });

  const sessions = liveQuery.data?.sessions ?? [];
  const heatPoints = heatQuery.data?.points ?? [];
  const liveCount = sessions.length;
  const heatCityCount = heatPoints.length;

  if (!authChecked) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-background text-sm text-muted-foreground">
        Indlæser…
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="relative h-screen w-screen overflow-hidden bg-background">
      <LazyCustomerMap
        sessions={sessions}
        heatPoints={heatPoints}
        showHeatmap={showHeatmap}
        heatPeriodLabel="sidste 30 dage"
      />

      {/* Floating control bar */}
      <div className="pointer-events-none absolute inset-x-0 top-0 z-[1000] flex items-start justify-between gap-3 p-4">
        <div className="pointer-events-auto rounded-lg border border-border bg-card/95 px-4 py-3 shadow-lg backdrop-blur">
          <h1 className="text-base font-semibold text-foreground">Customer Map — Live</h1>
          <p className="text-xs text-muted-foreground">
            Live opdaterer hvert 15. sek · Heat-map: top {heatCityCount} byer (30 dage)
          </p>
        </div>
        <div className="pointer-events-auto flex items-center gap-3 rounded-lg border border-border bg-card/95 px-3 py-2 shadow-lg backdrop-blur">
          <Badge variant="outline" className="gap-1.5">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
            </span>
            {liveCount} aktive nu
          </Badge>
          <div className="flex items-center gap-2 border-l border-border pl-3">
            <Flame size={14} className="text-orange-500" />
            <Label htmlFor="heat-toggle-fs" className="cursor-pointer text-xs">
              Heat-map
            </Label>
            <Switch
              id="heat-toggle-fs"
              checked={showHeatmap}
              onCheckedChange={setShowHeatmap}
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              liveQuery.refetch();
              heatQuery.refetch();
            }}
          >
            <RefreshCw size={14} className="mr-1.5" />
            Opdater
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (typeof window !== "undefined" && window.opener) window.close();
              else navigate({ to: "/admin/customer-map" });
            }}
            title="Luk"
          >
            <X size={16} />
          </Button>
        </div>
      </div>

      {/* Heat-map legend */}
      {showHeatmap && heatPoints.length > 0 ? (
        <div className="pointer-events-none absolute bottom-4 left-4 z-[1000] rounded-lg border border-border bg-card/95 px-3 py-2 text-xs shadow-lg backdrop-blur">
          <p className="mb-1.5 font-medium text-foreground">Besøgs-tæthed (30 dage)</p>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Lav</span>
            <div
              className="h-2 w-32 rounded"
              style={{
                background:
                  "linear-gradient(to right, #1e3a8a, #0ea5e9, #facc15, #f97316, #dc2626)",
              }}
            />
            <span className="text-muted-foreground">Høj</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
